/* eslint-disable max-lines */
import { useCallback, useState } from 'react';

import { useSetAtom } from 'jotai';

import { MicInputPreviewLabel } from '@heidi/capture';
import { Button } from '@heidi/design-system/button';
import {
  DialogDrawer,
  DialogDrawerContent,
  DialogDrawerTitle,
  DialogDrawerTrigger,
} from '@heidi/design-system/mobile-responsive-dialog-drawer';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@heidi/design-system/select';
import { TypographyP, TypographyP2 } from '@heidi/design-system/typography';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { RecordingStatus, SessionPages } from '@heidi/schema/ml-scribe';
import { sessionChooseMicInput, useQuerySessionById } from '@heidi/session/data';
import { cn, useIsMobile } from '@heidi/utils';
import { isEmpty } from 'lodash-es';
import { Check, Mic } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSessionPageHost } from '../../session-page/host-context';
import { VolumeBar } from '../volume-bar';

type Props = {
  hideValue?: boolean;
  className?: string;
  hideVolumeIndicator?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  iconClassName?: string;
};

const MicrophoneSelectorV1 = ({
  hideValue,
  className,
  hideVolumeIndicator,
  disabled,
  onClick,
  iconClassName,
}: Props) => {
  const intl = useIntl();
  const host = useSessionPageHost();
  const { setSelectedAudioSource } = host;
  const { audioSourceDevices, selectedAudioSource, volumeDetectLevel } =
    host.useAudioState();
  const { isRunning, hasStartedTimer } = host.useRecordingTimer();
  const { tutorialStepCurrentPage, onNextPage, panelOpen } = host.useTutorial();
  const shouldHighlightMicrophoneSelector =
    tutorialStepCurrentPage?.enum === SessionPages.CHECK_MICROPHONE && panelOpen;
  const isMobile = useIsMobile();
  const canChangeMicrophone = host.useCanChangeMicrophoneForCurrentSession();
  const sessionId = useCurrentSessionId();
  const { data: session } = useQuerySessionById({
    sessionId,
    select: (data) => {
      if (!data) return undefined;
      return {
        recording_status: data.recording_status,
      };
    },
  });
  const [open, setOpen] = useState<boolean>(false);
  const getFallbackMicrophoneValue = (index: number) => `microphone-${index + 1}`;
  const getMicrophoneOptionValue = useCallback(
    (device: MediaDeviceInfo, index: number) =>
      device.deviceId?.trim() || getFallbackMicrophoneValue(index),
    []
  );
  const handleValueChange = useCallback(
    (value: string) => {
      const selectedAudioDevice = audioSourceDevices.find(
        (device: MediaDeviceInfo, index: number) =>
          getMicrophoneOptionValue(device, index) === value
      );
      if (!selectedAudioDevice) {
        return;
      }
      sessionChooseMicInput(selectedAudioDevice.label);
      void setSelectedAudioSource(selectedAudioDevice);
      // updateAudioDeviceInRecording({
      //   sessionId: sessionId,
      //   isAbrubt: false,
      // });
    },
    [audioSourceDevices, setSelectedAudioSource, getMicrophoneOptionValue]
  );

  const noSoundDetected =
    volumeDetectLevel === 'noSound' || audioSourceDevices.length === 0;
  const getFallbackMicrophoneLabel = (index?: number) => {
    if (index === undefined) {
      return intl.formatMessage({
        id: 'cW6887Dc/X',
        defaultMessage: 'Microphone',
      });
    }

    return intl.formatMessage(
      {
        id: 'Fih8xeZhPm',
        defaultMessage: 'Microphone {index}',
      },
      { index: index + 1 }
    );
  };

  const setReadyToRequestMicPermissions = useSetAtom(
    host.readyToRequestMicPermissionsAtom
  );
  const selectedAudioSourceIndex = audioSourceDevices.findIndex((device) => {
    const deviceId = device.deviceId?.trim();
    const selectedDeviceId = selectedAudioSource?.deviceId?.trim();

    if (deviceId && selectedDeviceId) {
      return deviceId === selectedDeviceId;
    }

    return device.label === selectedAudioSource?.label;
  });
  const matchedSelectedAudioSource =
    selectedAudioSourceIndex >= 0
      ? audioSourceDevices[selectedAudioSourceIndex]
      : undefined;
  const selectedAudioSourceValue = matchedSelectedAudioSource
    ? getMicrophoneOptionValue(matchedSelectedAudioSource, selectedAudioSourceIndex)
    : undefined;

  // Hide the microphone selector when session resumed
  if (
    !isRunning &&
    hasStartedTimer &&
    session?.recording_status === RecordingStatus.IDLE
  ) {
    return null;
  }

  if (isMobile) {
    return (
      <DialogDrawer
        open={open}
        onOpenChange={(newOpen) => {
          setOpen(newOpen);
          if (newOpen) setReadyToRequestMicPermissions(true);
          if (
            !newOpen &&
            shouldHighlightMicrophoneSelector &&
            selectedAudioSource?.deviceId
          ) {
            onNextPage();
          }
        }}
      >
        <DialogDrawerTrigger>
          <div
            className={cn(
              'flex items-center gap-1 rounded-md border',
              noSoundDetected ? 'border-validation-destructive' : 'border-0',
              className
            )}
          >
            <Mic size={20} className={iconClassName} />
            {!hideValue && selectedAudioSource?.deviceId && (
              <div className="truncate">
                {selectedAudioSource.label || getFallbackMicrophoneLabel()}
              </div>
            )}
            {/*{!hideVolumeIndicator && (*/}
            <VolumeBar selectedAudioSourceDevice={selectedAudioSource} />
            {/*)}*/}
          </div>
        </DialogDrawerTrigger>

        <DialogDrawerContent hideCloseIcon className={'gap-4'}>
          <host.MobileModalNavBar
            onClose={() => setOpen(false)}
            title={
              <DialogDrawerTitle>
                <FormattedMessage id="N+xxAgMSxx" defaultMessage="Microphone input" />
              </DialogDrawerTitle>
            }
          />
          <div className="flex flex-col gap-2">
            {isEmpty(audioSourceDevices) && (
              <TypographyP2 className="items-center p-4 text-center text-text-secondary">
                <FormattedMessage
                  id="gk2+jobt0X"
                  defaultMessage="No input device found. Please check your permissions or input devices and refresh the page"
                />
              </TypographyP2>
            )}
            {audioSourceDevices.map((item, index) => (
              <Button
                size="lg"
                disableAutomaticSvgSizing
                key={item.deviceId}
                variant="ghost"
                className="justify-between"
                onClick={() => {
                  handleValueChange(getMicrophoneOptionValue(item, index));
                  setOpen(false);
                }}
              >
                <TypographyP>
                  {item.label || getFallbackMicrophoneLabel(index)}
                </TypographyP>
                {selectedAudioSource?.deviceId === item.deviceId && (
                  <Check className="size-4" />
                )}
              </Button>
            ))}
          </div>
        </DialogDrawerContent>
      </DialogDrawer>
    );
  }
  return (
    <Select
      value={selectedAudioSourceValue}
      onValueChange={handleValueChange}
      disabled={!canChangeMicrophone}
      onOpenChange={(open) => {
        onClick?.();
        if (open) setReadyToRequestMicPermissions(true);
        if (!open && shouldHighlightMicrophoneSelector && selectedAudioSource?.deviceId) {
          onNextPage();
        }
      }}
    >
      <SelectTrigger
        className={cn(
          'flex !h-auto w-auto gap-1 border bg-transparent p-1',
          noSoundDetected ? 'border-validation-destructive' : 'border-transparent',
          className
        )}
        hideSelectIcon={disabled}
      >
        <div
          className={cn(
            'overflow-hidden',
            !hideValue && 'flex w-full max-w-fit flex-1 items-center gap-1'
          )}
        >
          <Mic size={20} className={iconClassName} />
          {!hideValue && selectedAudioSource?.deviceId && (
            <div className="truncate">
              <SelectValue />
            </div>
          )}
        </div>

        {!hideVolumeIndicator && (
          <VolumeBar selectedAudioSourceDevice={selectedAudioSource} />
        )}
        <MicInputPreviewLabel label={selectedAudioSource?.label} />
      </SelectTrigger>

      {!disabled && (
        <SelectContent align="end">
          {isEmpty(audioSourceDevices) && (
            <TypographyP2 className="max-w-[200px] items-center p-4 text-center text-text-secondary">
              <FormattedMessage
                id="gk2+jobt0X"
                defaultMessage="No input device found. Please check your permissions or input devices and refresh the page"
              />
            </TypographyP2>
          )}
          <SelectGroup>
            {audioSourceDevices.map((item, index) => (
              <SelectItem
                className="pl-2 pr-8 [&>span]:left-auto [&>span]:right-2"
                key={item.deviceId}
                value={getMicrophoneOptionValue(item, index)}
              >
                {/* https://issues.chromium.org/issues/41288617 fix the chrome bug on Android webview*/}
                {item.label || getFallbackMicrophoneLabel(index)}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      )}
    </Select>
  );
};

export default MicrophoneSelectorV1;
